<template>
  <Dialog v-model:visible="open" modal header="Create A New Project">
    <!-- At project limit: show upgrade prompt instead of form -->
    <div v-if="isAtProjectLimit" class="space-y-4 pb-4">
      <div class="rounded-md bg-red-400/20 px-5 py-4 ring-1 ring-red-400/50 dark:bg-red-900/30 dark:ring-red-600/50">
        <div class="flex items-start gap-3">
          <ExclamationTriangleIcon class="mt-0.5 h-5 w-5 flex-shrink-0 text-red-600 dark:text-red-400" />
          <div class="space-y-1">
            <div class="font-semibold text-red-800 dark:text-red-200">
              Project limit reached
            </div>
            <div class="text-sm text-red-700 dark:text-red-300">
              You are using {{ planLimits.projects.current }} of {{ planLimits.projects.max }}
              projects on your {{ tierDisplayName(planTier) }} plan.
              <template v-if="nextTier">
                Upgrade to {{ tierDisplayName(nextTier) }} for up to {{ nextTierLimit }} projects.
              </template>
            </div>
          </div>
        </div>
      </div>
      <div class="flex justify-end">
        <router-link :to="{ name: ROUTES.UPGRADE }" @click="open = false">
          <Button label="Upgrade Plan" icon="pi pi-arrow-up-right" />
        </router-link>
      </div>
    </div>

    <!-- Normal form when under limit -->
    <form v-else @submit="createTeam" class="space-y-6 pb-8">
      <FormGroup label="Project Name">
        <InputText v-model="name" :disabled="isSubmitting" class="w-full" size="large" />
        <template #description>
          <span class="text-sm text-red-800">{{ errors.name }}</span>
        </template>
      </FormGroup>

      <FormGroup label="Website URL">
        <InputText v-model="website" :disabled="isSubmitting" class="w-full" size="large" />
        <template #description>
          <span v-if="errors.website" class="text-sm text-red-800">{{ errors.website }}</span>
          <span v-else-if="websiteResearchWarning" class="text-sm text-surface-600 dark:text-surface-300">
            {{ websiteResearchWarning }}
          </span>
        </template>
      </FormGroup>

      <p v-if="submitError" class="text-sm text-red-800 dark:text-red-300">
        {{ submitError }}
      </p>

      <Button
        type="submit"
        :disabled="isSubmitting"
        :label="isSubmitting ? 'Creating Project…' : 'Create Project'" />
    </form>
  </Dialog>
</template>

<script setup lang="ts">
import { useForm } from 'vee-validate'
import { useField } from 'vee-validate'
import z from 'zod'
import { toTypedSchema } from '@vee-validate/zod'
import type { TeamCreateAfterCreate } from '@/stores/teamStore'
import { ExclamationTriangleIcon } from '@heroicons/vue/24/outline'
import type { PlanTier } from '@/stores/subscriptionStore'
import { getWebsiteResearchIneligibilityReason } from '@/services/websiteResearchEligibility'
import { requestUuid } from '@/services/uuid'

const open = defineModel()
const props = withDefaults(defineProps<{ afterCreate?: TeamCreateAfterCreate }>(), {
  afterCreate: 'onboarding'
})
const subscriptionStore = useSubscriptionStore()
const { planLimits, planTier, isLegacy, isTrialing, isFreeAccount, isLegacyBilling } = storeToRefs(subscriptionStore)

const TIER_LIMITS: Record<string, number> = {
  grow: 1,
  scale: 3,
  pro: 10,
}

const tierDisplayName = (tier: PlanTier): string => {
  const names: Record<PlanTier, string> = { grow: 'Grow', scale: 'Scale', pro: 'Pro', legacy: 'Legacy' }
  return names[tier] ?? tier
}

const nextTier = computed<PlanTier | null>(() => {
  const order: PlanTier[] = ['grow', 'scale', 'pro']
  const idx = order.indexOf(planTier.value)
  if (idx < 0 || idx >= order.length - 1) return null
  return order[idx + 1]
})

const nextTierLimit = computed(() => nextTier.value ? TIER_LIMITS[nextTier.value] ?? null : null)

const isAtProjectLimit = computed(() => {
  // Per-team billing: no project limit (each project gets its own subscription, paywall at publish)
  if (!isLegacyBilling.value) return false
  if (isLegacy.value || isTrialing.value || isFreeAccount.value) return false
  return planLimits.value.projects.current >= planLimits.value.projects.max
})
const normalizeProjectWebsite = (value: string) => {
  const trimmed = value.trim()
  if (!trimmed) return trimmed

  const withProtocol = /^[a-z][a-z\d+\-.]*:\/\//i.test(trimmed)
    ? trimmed
    : `https://${trimmed}`

  try {
    const url = new URL(withProtocol)
    return `${url.protocol}//${url.host}`
  } catch {
    return withProtocol
  }
}


const newTeamSchema = toTypedSchema(
  z.object({
    name: z.string().min(1, 'Project name is required'),
    website: z
      .string()
      .transform(normalizeProjectWebsite)
      .pipe(
        z
          .string()
          .url({ message: 'Please enter a valid website address' })
          .refine(
            (val) => {
              try {
                const url = new URL(val)
                // Ensure there's at least one dot in the hostname (e.g., example.com)
                return url.hostname.includes('.') && !url.pathname.slice(1)
              } catch {
                return false
              }
            },
            { message: 'Please enter a valid domain (e.g., example.com)' }
          )
      )
  })
)

const { handleSubmit, errors, resetForm } = useForm({
  validationSchema: newTeamSchema,
  initialValues: {
    name: '',
    website: ''
  }
})

const { value: name } = useField('name')
const { value: website } = useField('website')
const WEBSITE_RESEARCH_WARNING =
  'We can still create your project, but we won’t be able to research that domain. We’ll start you with generic playbooks, and you can update the website and retry.'
const websiteResearchWarning = computed(() =>
  getWebsiteResearchIneligibilityReason(website.value || '') ? WEBSITE_RESEARCH_WARNING : null
)

const teamStore = useTeamStore()
const submitError = ref<string | null>(null)
const isSubmitting = ref(false)
const createRequestId = ref<string | null>(null)

watch(open, (isOpen) => {
  if (isOpen) return

  submitError.value = null
  isSubmitting.value = false
  createRequestId.value = null
  resetForm()
})

const createTeam = handleSubmit(async (values) => {
  if (isSubmitting.value) return

  isSubmitting.value = true
  submitError.value = null
  createRequestId.value ??= requestUuid()

  try {
    await teamStore.create(values, {
      afterCreate: props.afterCreate,
      clientRequestId: createRequestId.value
    })
    open.value = false
    createRequestId.value = null
    resetForm()
  } catch (error: any) {
    submitError.value = error?.response?.data?.message || 'We couldn’t create your project. Please try again.'
  } finally {
    isSubmitting.value = false
  }
})
</script>
